import { Link } from "@inertiajs/react"
import { ArrowUpRight, ChevronDown, ChevronUp } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { LinkedFlashcard } from "@/types/flashcard"

interface RelatedCardsProps {
  cards: LinkedFlashcard[]
  direction: "words" | "phrases"
  maxVisible?: number
}

export function RelatedCards({
  cards,
  direction,
  maxVisible = 5,
}: RelatedCardsProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  if (cards.length === 0) return null

  const visibleCards = isExpanded ? cards : cards.slice(0, maxVisible)
  const hiddenCount = cards.length - maxVisible
  const hasMore = hiddenCount > 0

  // Refined minimalism styling - industry-standard patterns
  const labelText = direction === "words" ? "Words" : "Phrases"
  const labelColors =
    direction === "words"
      ? "border-fuchsia-400/30 bg-fuchsia-950/20 text-fuchsia-400 dark:border-fuchsia-400/30 dark:bg-fuchsia-900/30 dark:text-fuchsia-300"
      : "border-violet-400/30 bg-violet-950/20 text-violet-400 dark:border-violet-400/30 dark:bg-violet-900/30 dark:text-violet-300"

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {/* Label pill - count indicator with refined styling */}
      <span
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${labelColors}`}
      >
        {cards.length} {labelText}
      </span>

      {/* Linked card pills - clickable with refined hover states */}
      {visibleCards.map((card) => (
        <Tooltip key={card.uuid}>
          <TooltipTrigger asChild>
            <Link href={`/flashcards/${card.uuid}`} className="group/pill">
              <span className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 transition-all duration-200 hover:border-slate-400 hover:bg-slate-200 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300 dark:hover:border-slate-600 dark:hover:bg-slate-800 dark:hover:shadow-sm">
                <span className="font-thai">
                  {card.backRomanization ?? card.backText}
                </span>
                <ArrowUpRight className="size-3 opacity-0 transition-opacity duration-200 group-hover/pill:opacity-100" />
              </span>
            </Link>
          </TooltipTrigger>
          <TooltipContent className="text-center">
            <div className="space-y-0.5">
              <p className="font-thai text-sm">{card.backText}</p>
              {card.backRomanization && (
                <p className="text-muted-foreground text-xs">
                  {card.backRomanization}
                </p>
              )}
              {card.frontText && (
                <p className="text-muted-foreground text-xs italic">
                  "{card.frontText}"
                </p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      ))}

      {/* Expand button */}
      {hasMore && !isExpanded && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(true)}
          className="text-muted-foreground hover:text-foreground h-5 px-1.5 text-xs"
        >
          +{hiddenCount}
          <ChevronDown className="ml-0.5 size-3" />
        </Button>
      )}

      {/* Collapse button */}
      {isExpanded && hasMore && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(false)}
          className="text-muted-foreground hover:text-foreground h-5 px-1.5 text-xs"
        >
          less
          <ChevronUp className="ml-0.5 size-3" />
        </Button>
      )}
    </div>
  )
}
